<script lang="ts">
  // See https://tfhub.dev/google/spice/2
  import * as tf from '@tensorflow/tfjs';
  import { leftPad } from 'shared/src/utils/pad';
  import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
  import Note from '../note';

  const NUM_INPUT_SAMPLES = 1024;
  const MODEL_SAMPLE_RATE = 16000;
  const PT_OFFSET = 25.58;
  const PT_SLOPE = 63.07;
  const MODEL_URL = 'https://tfhub.dev/google/tfjs-model/spice/2/default/1';
  let model: tf.GraphModel;

  async function startDemo() {
    if (typeof window === 'undefined') return;
    model = await tf.loadGraphModel(MODEL_URL, { fromTFHub: true });
    window.navigator.mediaDevices
      .getUserMedia({ audio: true, video: false })
      .then(handleSuccess)
      .catch(handleError);
  }

  function handleError(err: any) {
    console.log(err);
  }

  function confidenceToColor(confidence: number) {
    const red = 1 * (1 - confidence);
    const redHex = Math.round(255 * red).toString(16);
    const green = confidence;
    const greenHex = Math.round(255 * green).toString(16);
    // console.log(`${confidence} ${redHex} ${greenHex}`);
    const confidenceHex = Math.round(255 * confidence).toString(16);
    return `#${leftPad(redHex, 2, '0')}${leftPad(greenHex, 2, '0')}00${leftPad(
      confidenceHex,
      2,
      '0',
    )}`;
  }

  function getPitchHz(modelPitch: number) {
    const fmin = 10.0;
    const binsPerOctave = 12.0;
    const cqtBin = modelPitch * PT_SLOPE + PT_OFFSET;
    return fmin * 2.0 ** ((1.0 * cqtBin) / binsPerOctave);
  }

  let estimates: { hz: number; midi: number; confidence: number; y: number; color: string }[] = [];

  function handleSuccess(stream: MediaStream) {
    const context = new AudioContext({
      latencyHint: 'playback',
      sampleRate: MODEL_SAMPLE_RATE,
    });

    const source = context.createMediaStreamSource(stream);
    const processor = context.createScriptProcessor(
      NUM_INPUT_SAMPLES,
      /* num_inp_channels= */ 1,
      /* num_out_channels= */ 1,
    );

    // Converts audio to mono.
    processor.channelInterpretation = 'speakers';
    processor.channelCount = 1;

    // Runs processor on audio source.
    source.connect(processor);
    processor.connect(context.destination);

    processor.onaudioprocess = function handleAudio(e: AudioProcessingEvent) {
      const inputData = e.inputBuffer.getChannelData(0);
      const input = tf.reshape(tf.tensor(inputData), [NUM_INPUT_SAMPLES]);
      const output = model.execute({ input_audio_samples: input });
      const uncertainties = output[0].dataSync();
      const pitches = output[1].dataSync();

      for (let i = 0; i < pitches.length; ++i) {
        const hz = getPitchHz(pitches[i]);
        const confidence = 1 - uncertainties[i];
        const color = confidenceToColor(confidence);
        const midi = Note.frequencyToMidi(hz);
        const y = midiToY(midi);
        const datum = { hz, midi, confidence: 1 - uncertainties[i], y, color };
        if (estimates.length < 200) {
          estimates = [...estimates, datum];
          estimates.push(datum);
        } else {
          estimates = [...estimates.slice(1), datum];
        }
        // console.log(
        //   `Pitch: ${pitches[i]}, Confidence: ${Math.round(100 * (1 - uncertainties[i]))} `,
        // );
        // if (confidence < CONF_THRESHOLD) {
        //   continue;
        // }
        // console.log(getPitchHz(pitches[i]));
      }
    };
  }

  const trebleLines: NoteName[] = ['E', 'G', 'B', 'D5', 'F5'];
  const trebleLineMidis = trebleLines.map((n) => new Note(n).getMidiNumber());

  const bassLines: NoteName[] = ['G2', 'B2', 'D3', 'F3', 'A3'];
  const bassLineMidis = bassLines.map((n) => new Note(n as NoteName).getMidiNumber());

  const lowestMidi = new Note('C2').getMidiNumber();
  const highestMidi = new Note('C6').getMidiNumber();
  const svgWidth = 200;

  const halfStepHeight = 4;

  function midiToY(midi: number) {
    return halfStepHeight * (highestMidi - midi);
  }
</script>

<button on:click={startDemo}>Start Demo</button>

<svg class="w-full" viewBox="0 0 {svgWidth} {midiToY(lowestMidi)}">
  {#each [...trebleLineMidis, ...bassLineMidis] as midi}
    <line x1="0" y1={midiToY(midi)} x2={svgWidth} y2={midiToY(midi)} stroke="#999999" />
  {/each}

  {#each estimates as estimate, i}
    <circle cx={i} cy={estimate.y} r={halfStepHeight} fill={estimate.color} />
  {/each}
</svg>
